import React, { useState, useEffect } from "react";
import LoadingComponent from "./loading";
import { format } from "date-fns";

const weatherGradientByCondition = {
  clear: "from-sky-400/35 via-yellow-300/35 to-amber-200/35",
  rain: "from-slate-500/30 via-gray-300/35 to-white/35",
  drizzle: "from-slate-500/30 via-gray-300/35 to-white/35",
  thunderstorm: "from-slate-700/35 via-blue-400/25 to-gray-200/35",
  snow: "from-white/60 via-slate-100/55 to-gray-200/45",
  clouds: "from-gray-300/30 via-slate-300/35 to-gray-200/35",
  mist: "from-gray-300/35 via-slate-200/35 to-white/35",
  haze: "from-gray-300/35 via-slate-200/35 to-white/35",
  fog: "from-gray-300/35 via-slate-200/35 to-white/35",
  smoke: "from-gray-400/35 via-slate-300/35 to-gray-200/35",
};

const getWeatherGradientClass = (condition = "") => {
  const key = condition.toLowerCase();
  return weatherGradientByCondition[key] || "from-blue-500/20 via-sky-400/20 to-emerald-400/20";
};


const Weather = () => {
  const [weatherData, setWeatherData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [city, setCity] = useState("");

  useEffect(() => {
    const fetchWeather = async () => {
      setIsLoading(true);
      let resolvedCity = "";

      try {
        const company = await fetch("https://api.insightos.co.uk/api/company", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        const companyData = await company.json();
        
        console.log("Fetched company data:", companyData);
        resolvedCity = (
          companyData?.data?.[0]?.data?.City ||
          companyData?.data?.[0]?.City ||
          ""
        )?.trim();
        console.log("Resolved city from company data:", resolvedCity);
        setCity(resolvedCity);
      } catch (error) {
        console.error("Error fetching company data:", error);
        setIsLoading(false);
        return;
      }

      try {
        const apiKey = "1e83f73cc624c5a3fdf20c4eb0b3a261";
        const url = `https://api.openweathermap.org/data/2.5/weather?q=${encodeURIComponent(resolvedCity)}&appid=${apiKey}`;
        const response = await fetch(url);
        const data = await response.json();

        if (!response.ok || String(data?.cod) === "404") {
          setWeatherData(null);
          setIsLoading(false);
          return;
        }

        setWeatherData(data);
      } catch (error) {
        console.error("Error fetching weather data:", error);
        setWeatherData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWeather();
  }, []);

  useEffect(() => {
    let hourlyTimer;
    const syncToHour = setTimeout(() => {
      setCurrentTime(new Date());
      hourlyTimer = setInterval(() => {
        setCurrentTime(new Date());
      }, 60 * 60 * 1000);
    }, (60 - new Date().getMinutes()) * 60 * 1000 - new Date().getSeconds() * 1000);

    return () => {
      clearTimeout(syncToHour);
      if (hourlyTimer) clearInterval(hourlyTimer);
    };
  }, []);

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (!weatherData?.main || !weatherData?.weather?.length) {
    return <p>Weather data not found for {city || "your city"}.</p>;
  }

  const { main, weather } = weatherData;
  const temp = Math.round(main.temp - 273.15); // Convert kelvin to celsius
  const weatherIconCode = weather[0].icon;
  const weatherLabel = weather[0].main;
  const weatherGradientClass = getWeatherGradientClass(weatherLabel);
  const formattedDate = format(currentTime, "EEEE do MMMM yyyy");

  return (
    <div className="space-y-3 p-2 sm:p-3">
      <style>{`
        @keyframes weatherGradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4 sm:items-stretch">
        <div
          className={`relative flex items-center gap-2 overflow-hidden rounded-2xl border border-white/35 bg-gradient-to-br ${weatherGradientClass} px-3 py-2.5 shadow-[0_14px_35px_rgba(15,23,42,0.35),inset_0_1px_0_rgba(255,255,255,0.35)] backdrop-blur-xl sm:col-span-3 sm:gap-3 sm:px-4 sm:py-3`}
          style={{ backgroundSize: "200% 200%", animation: "weatherGradientShift 20s ease-in-out infinite" }}
        >
          <div className="pointer-events-none absolute -top-8 -left-8 h-24 w-24 rounded-full bg-white/25 blur-2xl " />
          <div className="pointer-events-none absolute -bottom-10 -right-8 h-24 w-24 rounded-full bg-sky-300/20 blur-2xl" />
          <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-white/85 bg-sky-300/35 ring-2 ring-slate-800/20 shadow-[0_0_0_4px_rgba(255,255,255,0.22),0_8px_18px_rgba(15,23,42,0.35)] sm:h-12 sm:w-12">
            <img
              src={`https://openweathermap.org/img/wn/${weatherIconCode}@2x.png`}
              alt={weatherLabel}
              width={28}
              height={28}
              className="drop-shadow-md"
            />
          </div>
          <div className="relative z-10 flex min-w-0 flex-1 flex-col leading-tight">
            <span className="truncate text-[11px] font-semibold uppercase tracking-wide text-blue-600 sm:text-xs">{city || "Unknown City"}</span>
            <span className="text-lg font-extrabold text-blue-800/90 sm:text-xl">{temp}°C</span>
          </div>
          <span className="relative z-10 ml-1 max-w-[40vw] shrink-0 truncate rounded-full bg-blue-800/80 px-2 py-1 text-[10px] font-semibold text-white shadow-md sm:ml-2 sm:max-w-none sm:px-3 sm:text-xs">
            {weatherLabel}
          </span>
        </div>

        <div className="relative flex items-center justify-center overflow-hidden rounded-2xl border border-white/35 bg-gradient-to-br from-sky-500/25 via-blue-500/20 to-emerald-500/20 px-2.5 py-2.5 shadow-[0_14px_35px_rgba(15,23,42,0.35),inset_0_1px_0_rgba(255,255,255,0.35)] backdrop-blur-xl sm:col-span-1 sm:px-3 sm:py-3">
          <div className="pointer-events-none absolute -top-8 -right-8 h-24 w-24 rounded-full bg-white/25 blur-2xl" />
          <div className="relative z-10 flex flex-col items-center leading-tight">
            <span className="text-base font-bold tabular-nums text-blue-800/90 sm:text-xl">
              {currentTime.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-2xl border border-white/35 bg-violet-600 shadow-[0_14px_35px_rgba(15,23,42,0.35),inset_0_1px_0_rgba(255,255,255,0.35)]">
        <div className="relative z-10 flex w-full items-center justify-center px-4 py-2.5 text-white text-center">
          <span className="text-xs font-semibold tracking-wide drop-shadow-[0_1px_2px_rgba(2,6,23,0.9)] sm:text-sm">{formattedDate}</span>
        </div>
      </div>
    </div>
  );
};

export default Weather;
